/**
 * AdminTenantDetail — Süperadmin merchant detay sayfası.
 *
 * 5 sekme (TR):
 *   1. Genel Bakış  — 4 KPI + 3 sağlık sinyali
 *   2. Hesap        — Firma + sahip + takım + login geçmişi
 *   3. Lisans       — Plan detayları, plan değiştir, +X saat uzat
 *   4. Entegrasyon  — Head/Meta test + XML scheduler (daily / every X / weekdays)
 *   5. Trafik       — Aylık kullanım + 30 günlük timeline
 *
 * Backend: GET /admin/tenants/{id} tek atışta tüm veriyi döner.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Building2, Loader2, Users, Package, Settings2, Activity,
    XCircle, ArrowRightCircle, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, Mono } from "@/components/design-system";
import { ADMIN_PATH } from "@/lib/admin-path";
import { PLAN_META, HEALTH_META, readError } from "./_tenant-helpers";
import TenantOverviewTab from "./tenant-detail/TenantOverviewTab";
import TenantAccountTab from "./tenant-detail/TenantAccountTab";
import TenantLicenseTab from "./tenant-detail/TenantLicenseTab";
import TenantIntegrationTab from "./tenant-detail/TenantIntegrationTab";
import TenantTrafficTab from "./tenant-detail/TenantTrafficTab";

export default function AdminTenantDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [impersonating, setImpersonating] = useState(false);
    const [tab, setTab] = useState("overview");

    const load = useCallback(async () => {
        try {
            const { data } = await adminApi.get(`/admin/tenants/${id}`);
            setData(data);
            setError(null);
        } catch (err) {
            setError(readError(err));
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const impersonate = useCallback(async () => {
        if (impersonating || !data) return;
        setImpersonating(true);
        try {
            await adminApi.post(`/admin/impersonate/${data.id}`);
            toast.success(`${data.company_name} paneline geçildi`);
            window.location.assign("/app");
        } catch (err) {
            toast.error(readError(err));
            setImpersonating(false);
        }
    }, [data, impersonating]);

    if (error) {
        return (
            <div className="space-y-4" data-testid="admin-tenant-detail-error">
                <BackButton onClick={() => nav(`${ADMIN_PATH}/overview`)} />
                <Card className="p-10 text-center text-sm text-rose-600">
                    <XCircle className="h-6 w-6 mx-auto mb-2" />
                    {error}
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="space-y-4" data-testid="admin-tenant-detail-loading">
                <BackButton onClick={() => nav(`${ADMIN_PATH}/overview`)} />
                <div className="p-10 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                    Hesap yükleniyor…
                </div>
            </div>
        );
    }

    const health = computeHealth(data);
    const healthMeta = HEALTH_META[health] || HEALTH_META.yellow;
    const planMeta = PLAN_META[data.license?.plan === "sayz" ? data.license?.billing_cycle : (data.license?.plan || "trial")] || PLAN_META.trial;

    return (
        <div className="space-y-6" data-testid="admin-tenant-detail-page">
            <BackButton onClick={() => nav(`${ADMIN_PATH}/overview`)} />

            {/* Header kart */}
            <Card className="p-5" data-testid="tenant-detail-header">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-lg border border-border bg-muted/40 grid place-items-center shrink-0">
                            <Building2 className="h-5 w-5" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight truncate">
                                    {data.company_name || "—"}
                                </h1>
                                <span
                                    data-testid={`tenant-detail-health-${health}`}
                                    title={healthMeta.label}
                                    className={`inline-block h-2 w-2 rounded-full ring-4 ${healthMeta.dot} ${healthMeta.ring}`}
                                />
                                <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${planMeta.tone}`}>
                                    <span className={`h-1 w-1 rounded-full ${planMeta.dot}`} />
                                    {planMeta.label}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                                <span className="truncate">{data.site_domain}</span>
                                <span>·</span>
                                <Mono>{data.id}</Mono>
                                <CopyBtn text={data.id} />
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            variant="outline"
                            onClick={impersonate}
                            disabled={!data.owner || impersonating}
                            className="h-9 text-[12px]"
                            data-testid="tenant-detail-impersonate"
                        >
                            {impersonating ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <ArrowRightCircle className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} />
                            )}
                            Panele Geç
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Sekmeler */}
            <Tabs value={tab} onValueChange={setTab} className="space-y-5" data-testid="tenant-detail-tabs">
                <TabsList className="grid grid-cols-5 w-full md:w-fit">
                    <TabsTrigger value="overview" data-testid="tab-overview">
                        <Activity className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Genel Bakış
                    </TabsTrigger>
                    <TabsTrigger value="account" data-testid="tab-account">
                        <Users className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Hesap
                    </TabsTrigger>
                    <TabsTrigger value="license" data-testid="tab-license">
                        <Package className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Lisans
                    </TabsTrigger>
                    <TabsTrigger value="integration" data-testid="tab-integration">
                        <Settings2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Entegrasyon
                    </TabsTrigger>
                    <TabsTrigger value="traffic" data-testid="tab-traffic">
                        <Activity className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} /> Trafik
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                    <TenantOverviewTab data={data} health={health} onJumpTab={setTab} />
                </TabsContent>
                <TabsContent value="account">
                    <TenantAccountTab
                        data={data}
                        onDeleted={() => nav(ADMIN_PATH, { replace: true })}
                    />
                </TabsContent>
                <TabsContent value="license">
                    <TenantLicenseTab data={data} onRefresh={load} />
                </TabsContent>
                <TabsContent value="integration">
                    <TenantIntegrationTab data={data} onRefresh={load} />
                </TabsContent>
                <TabsContent value="traffic">
                    <TenantTrafficTab data={data} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

/** Composite sağlık rozeti — list endpoint ile aynı mantık, UI tarafında tekrar hesaplanır. */
function computeHealth(d) {
    const lic = d.license || {};
    const inst = d.install || {};
    const xml = d.xml || {};
    const usagePct = d.traffic?.usage_pct || 0;
    const headBad = inst.head_status === "disconnected";
    const xmlStale = xml.hours_since_sync != null && xml.hours_since_sync > 48;
    const xmlFailed = xml.last_sync_status === "failed";
    const expired = lic.is_expired;
    const daysLeft = lic.days_remaining;

    if (expired || (xml.url && xmlFailed)) return "red";
    if (headBad || xmlStale || (daysLeft != null && daysLeft <= 3) || usagePct >= 80) return "yellow";
    return "green";
}

function BackButton({ onClick }) {
    return (
        <button
            onClick={onClick}
            className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="tenant-detail-back"
        >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            Tüm hesaplar
        </button>
    );
}

function CopyBtn({ text }) {
    const copy = (e) => {
        e.stopPropagation();
        if (!text) return;
        navigator.clipboard?.writeText(text);
        toast.success("Kopyalandı");
    };
    return (
        <button
            onClick={copy}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Kopyala"
            data-testid="tenant-id-copy"
        >
            <Copy className="h-3 w-3" strokeWidth={1.75} />
        </button>
    );
}
